import React, { useState } from "react";
import { useQuery, useQueries, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { gatewaysApi, clientsApi } from "@/lib/apiClient";
import { PageContainer, PageHeader } from "@/components/Layout";
import { Pager, usePager } from "@/components/Pager";
import { Mono, CodePill, StatusDot } from "@/components/Atoms";
import { Plus, Trash2, Pencil, ArrowRight, Search } from "lucide-react";
import { toast } from "sonner";
import { useCan, useScopedClientIds, canFor } from "@/lib/rbac";
import { useAuth } from "@/store/auth";

const TABS = [
  { k: "instances", label: "Gateway instances", cap: null },
  { k: "hw", label: "Hardware models", cap: "read:gateway_hw" },
  { k: "messages", label: "Live messages", cap: "read:gw_messages" },
];

export default function Gateways() {
  const canReadHw = useCan("read:gateway_hw");
  const canReadMsgs = useCan("read:gw_messages");
  const visibleTabs = TABS.filter((t) => {
    if (t.k === "hw") return canReadHw;
    if (t.k === "messages") return canReadMsgs;
    return true;
  });
  const [tab, setTab] = useState(visibleTabs[0]?.k || "instances");
  return (
    <PageContainer>
      <PageHeader title="Gateways" subtitle="Hardware models, gateway instances and live message stream." />
      <div className="flex gap-1 border-b border-zinc-200 mb-4" role="tablist">
        {visibleTabs.map((t) => (
          <button
            key={t.k}
            data-testid={`gw-tab-${t.k}`}
            role="tab"
            aria-selected={tab === t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              tab === t.k ? "border-[#3730A3] text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "instances" && <Instances />}
      {tab === "hw" && canReadHw && <Hardware />}
      {tab === "messages" && canReadMsgs && <Messages />}
    </PageContainer>
  );
}

function Hardware() {
  const qc = useQueryClient();
  const canWrite = useCan("write:gateway_hw");
  const hw = useQuery({ queryKey: ["gw-hw"], queryFn: gatewaysApi.hwList });
  const [modal, setModal] = useState(null);
  const inval = () => qc.invalidateQueries({ queryKey: ["gw-hw"] });
  const cM = useMutation({ mutationFn: gatewaysApi.hwCreate, onSuccess: () => { toast.success("HW added"); inval(); setModal(null); } });
  const uM = useMutation({ mutationFn: ({ id, data }) => gatewaysApi.hwUpdate(id, data), onSuccess: () => { toast.success("HW updated"); inval(); setModal(null); } });
  const dM = useMutation({ mutationFn: gatewaysApi.hwRemove, onSuccess: () => { toast.success("HW removed"); inval(); } });

  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-zinc-200 flex justify-end">
        {canWrite && (
          <button data-testid="gw-hw-new" onClick={() => setModal({ mode: "create", row: { canonical_hw: "", name: "", vendor: "", protocol: "BLE" } })} className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm hover:bg-indigo-900 inline-flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> New HW</button>
        )}
      </div>
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
          <tr>
            <th className="text-left p-3 font-semibold">Canonical HW</th>
            <th className="text-left p-3 font-semibold">Name</th>
            <th className="text-left p-3 font-semibold">Vendor</th>
            <th className="text-left p-3 font-semibold">Protocol</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {hw.data?.map((h) => (
            <tr key={h.id} className="border-t border-zinc-100">
              <td className="p-3"><Mono>{h.canonical_hw}</Mono></td>
              <td className="p-3">{h.name}</td>
              <td className="p-3 text-zinc-600">{h.vendor}</td>
              <td className="p-3"><CodePill>{h.protocol}</CodePill></td>
              <td className="p-3 text-right">
                {canWrite && (
                  <>
                    <button onClick={() => setModal({ mode: "edit", row: h })} className="p-1.5 rounded hover:bg-zinc-100"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => { if (window.confirm("Delete HW?")) dM.mutate(h.id); }} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {modal && (
        <FormModal title={modal.mode === "create" ? "New hardware" : "Edit hardware"} initial={modal.row} fields={[["canonical_hw","Canonical HW"],["name","Name"],["vendor","Vendor"],["protocol","Protocol (BLE/LoRaWAN)"]]} onCancel={() => setModal(null)} onSave={(form) => modal.mode==="create" ? cM.mutate(form) : uM.mutate({id: modal.row.id, data: form})} />
      )}
    </div>
  );
}

function Instances() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const clientFilter = params.get("client") || "";

  const canWrite = useCan("write:gateways");
  const scoped = useScopedClientIds();
  const role = useAuth((s) => s.role);
  const clientIds = useAuth((s) => s.clientIds);

  const [search, setSearch] = useState("");
  const [hwFilter, setHwFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "online" | "offline" | ""

  const list = useQuery({
    queryKey: ["gateways", clientFilter],
    queryFn: () => gatewaysApi.list(clientFilter ? { client_id: clientFilter } : undefined),
  });
  const clients = useQuery({ queryKey: ["clients"], queryFn: clientsApi.list });
  const hw = useQuery({ queryKey: ["gw-hw"], queryFn: gatewaysApi.hwList });
  const [modal, setModal] = useState(null);
  const inval = () => qc.invalidateQueries({ queryKey: ["gateways"] });
  const cM = useMutation({ mutationFn: gatewaysApi.create, onSuccess: () => { toast.success("Gateway added"); inval(); setModal(null); } });
  const uM = useMutation({ mutationFn: ({ id, data }) => gatewaysApi.update(id, data), onSuccess: () => { toast.success("Gateway updated"); inval(); setModal(null); } });
  const dM = useMutation({ mutationFn: gatewaysApi.remove, onSuccess: () => { toast.success("Gateway removed"); inval(); } });

  // Live status — one query per gateway, polled every 60s. Only fetched for
  // roles the backend allows: admin/service/viewer. Operators get 403 here
  // so we skip the queries entirely to avoid a wall of error toasts.
  // (Both /api/gateway/status and /api/gateway/messages are monitoring
  //  endpoints with the same role scope on the backend.)
  const canReadStatus = useCan("read:gw_messages");
  const gateways = list.data || [];
  // Restrict list to operator scope.
  const scopedGateways = React.useMemo(() => {
    if (scoped == null) return gateways;
    const set = new Set(scoped);
    return gateways.filter((g) => set.has(String(g.client_id)));
  }, [gateways, scoped]);
  const scopedClients = React.useMemo(() => {
    if (scoped == null) return clients.data || [];
    const set = new Set(scoped);
    return (clients.data || []).filter((c) => set.has(String(c.id)));
  }, [clients.data, scoped]);
  const canRowWrite = (g) => canFor({ role, clientIds }, "write:gateways", g.client_id);

  const statusQueries = useQueries({
    queries: scopedGateways.map((g) => ({
      queryKey: ["gw-status", g.mac],
      queryFn: () => gatewaysApi.status(g.mac),
      refetchInterval: 60000,
      retry: 0,
      staleTime: 30000,
      enabled: canReadStatus,
    })),
  });
  const statusByMac = Object.fromEntries(
    scopedGateways.map((g, i) => [g.mac, statusQueries[i]?.data])
  );

  const resolveOnline = (g) => {
    const s = statusByMac[g.mac];
    if (s && typeof s.online === "boolean") return s.online;
    // Fallback: heuristic from last_seen if backend status endpoint isn't available.
    if (!g.last_seen) return false;
    return Date.now() - new Date(g.last_seen).getTime() < 5 * 60 * 1000;
  };

  const filteredRows = scopedGateways
    .filter((g) => !clientFilter || g.client_id === clientFilter)
    .filter((g) => {
      if (!search) return true;
      const s = search.toLowerCase();
      return (g.name || "").toLowerCase().includes(s) || (g.mac || "").toLowerCase().includes(s);
    })
    .filter((g) => !hwFilter || g.hw_id === hwFilter)
    .filter((g) => {
      if (!statusFilter) return true;
      const online = resolveOnline(g);
      return statusFilter === "online" ? online : !online;
    });
  const pager = usePager(filteredRows, { defaultSize: 50, sizes: [25, 50, 100] });

  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-zinc-200 grid grid-cols-1 md:grid-cols-5 gap-3 items-center">
        <div className="relative md:col-span-2">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-zinc-400" />
          <input
            data-testid="gw-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or MAC…"
            className="w-full pl-8 pr-3 py-2 text-sm border border-zinc-200 rounded-md focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3] outline-none"
          />
        </div>
        <select
          data-testid="gw-filter-client"
          value={clientFilter}
          onChange={(e) => navigate(e.target.value ? `/gateways?client=${e.target.value}` : "/gateways")}
          className="border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
        >
          <option value="">All clients</option>
          {scopedClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select
          data-testid="gw-filter-hw"
          value={hwFilter}
          onChange={(e) => setHwFilter(e.target.value)}
          className="border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
        >
          <option value="">All hardware</option>
          {hw.data?.map((h) => <option key={h.id} value={h.id}>{h.canonical_hw}</option>)}
        </select>
        <select
          data-testid="gw-filter-status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
        >
          <option value="">All statuses</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
        </select>
      </div>

      <div className="px-4 py-2.5 border-b border-zinc-200 flex justify-between items-center gap-3">
        <div className="text-xs text-zinc-500 font-mono">{filteredRows.length} of {scopedGateways.length} gateways</div>
        {canWrite && (
          <button
            data-testid="gw-new"
            onClick={() => setModal({ mode: "create", row: { mac: "", name: "", client_id: scopedClients[0]?.id, hw_id: hw.data?.[0]?.id } })}
            className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm hover:bg-indigo-900 inline-flex items-center gap-1.5"
          ><Plus className="w-3.5 h-3.5" /> New gateway</button>
        )}
      </div>

      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
          <tr>
            <th className="text-left p-3 font-semibold">Name</th>
            <th className="text-left p-3 font-semibold">MAC</th>
            <th className="text-left p-3 font-semibold">Client</th>
            <th className="text-left p-3 font-semibold">HW</th>
            <th className="text-left p-3 font-semibold">Status</th>
            <th className="w-32"></th>
          </tr>
        </thead>
        <tbody>
          {pager.pageRows.map((g) => {
            const online = resolveOnline(g);
            return (
              <tr key={g.id} className="border-t border-zinc-100 hover:bg-zinc-50/60" data-testid={`gw-row-${g.id}`}>
                <td className="p-3 font-medium">{g.name}</td>
                <td className="p-3"><Mono>{g.mac}</Mono></td>
                <td className="p-3 text-zinc-600">{clients.data?.find((c) => c.id === g.client_id)?.name || g.client_id}</td>
                <td className="p-3"><CodePill>{hw.data?.find((h) => h.id === g.hw_id)?.canonical_hw || g.hw_id}</CodePill></td>
                <td className="p-3"><StatusDot online={online} label={online ? "online" : "offline"} /></td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      data-testid={`gw-open-${g.id}`}
                      onClick={() => navigate(`/gateways/${g.id}`)}
                      className="p-1.5 rounded hover:bg-zinc-100"
                      title="Open"
                    ><ArrowRight className="w-4 h-4" /></button>
                    {canRowWrite(g) && (
                      <>
                        <button onClick={() => setModal({ mode: "edit", row: g })} className="p-1.5 rounded hover:bg-zinc-100"><Pencil className="w-4 h-4" /></button>
                        <button data-testid={`gw-delete-${g.id}`} onClick={() => { if (window.confirm("Delete gateway?")) dM.mutate(g.id); }} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {pager.total === 0 && (
            <tr><td colSpan={6} className="py-10 text-center text-sm text-zinc-500">No gateways match the filters.</td></tr>
          )}
        </tbody>
      </table>
      <Pager {...pager} testidPrefix="gw-pager" />
      {modal && (
        <FormModal
          title={modal.mode === "create" ? "New gateway" : "Edit gateway"}
          initial={modal.row}
          fields={[
            ["name", "Name"],
            ["mac", "MAC"],
          ]}
          selects={[
            ["client_id", "Client", scopedClients.map((c) => [c.id, c.name]) || []],
            ["hw_id", "Hardware", hw.data?.map((h) => [h.id, h.canonical_hw]) || []],
          ]}
          onCancel={() => setModal(null)}
          onSave={(form) => modal.mode === "create" ? cM.mutate(form) : uM.mutate({ id: modal.row.id, data: form })}
        />
      )}
    </div>
  );
}

function Messages() {
  const [filter, setFilter] = useState({ gw_mac: "", flag: "" });
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);
  const list = useQuery({ queryKey: ["gw-messages", filter], queryFn: () => gatewaysApi.messages(filter) });
  const gws = useQuery({ queryKey: ["gateways"], queryFn: () => gatewaysApi.list() });

  const rows = list.data || [];
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const visible = rows.slice((page - 1) * pageSize, page * pageSize);

  // Reset to first page when filters or page size change.
  React.useEffect(() => { setPage(1); }, [filter.gw_mac, filter.flag, pageSize]);

  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-zinc-200 grid grid-cols-2 md:grid-cols-4 gap-3">
        <select
          data-testid="gw-msg-filter-mac"
          value={filter.gw_mac}
          onChange={(e) => setFilter({ ...filter, gw_mac: e.target.value })}
          className="border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
        >
          <option value="">All gateways</option>
          {gws.data?.map((g) => <option key={g.id} value={g.mac}>{g.name} · {g.mac}</option>)}
        </select>
        <select
          data-testid="gw-msg-filter-flag"
          value={filter.flag}
          onChange={(e) => setFilter({ ...filter, flag: e.target.value })}
          className="border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
        >
          <option value="">All flags</option>
          {["0x01", "0x02", "0x03", "0x04"].map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <div /> {/* spacer */}
        <div className="flex items-center justify-end gap-2 text-xs text-zinc-600">
          <label className="text-zinc-500 uppercase tracking-wider font-mono text-[10px]">rows</label>
          <select
            data-testid="gw-msg-page-size"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="border border-zinc-200 rounded-md px-2 py-1 text-xs bg-white"
          >
            {[20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
          <tr>
            <th className="text-left p-3 font-semibold">Received</th>
            <th className="text-left p-3 font-semibold">GW MAC</th>
            <th className="text-left p-3 font-semibold">Flag</th>
            <th className="text-left p-3 font-semibold">Payload (hex)</th>
            <th className="text-left p-3 font-semibold">Parsed</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((m) => (
            <tr key={m.id} className="border-t border-zinc-100" data-testid={`gw-msg-row-${m.id}`}>
              <td className="p-3 text-xs font-mono text-zinc-500">{new Date(m.received_at).toLocaleString()}</td>
              <td className="p-3"><Mono>{m.gw_mac}</Mono></td>
              <td className="p-3"><CodePill>{m.flag}</CodePill></td>
              <td className="p-3"><Mono className="text-zinc-600 truncate block max-w-[500px]">{m.payload_hex}</Mono></td>
              <td className="p-3"><Mono className="text-zinc-700">{JSON.stringify(m.parsed)}</Mono></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-sm text-zinc-500">No messages match the filters.</td></tr>}
        </tbody>
      </table>

      <div className="px-4 py-3 border-t border-zinc-200 flex items-center justify-between text-xs text-zinc-500">
        <div className="font-mono">
          showing {rows.length === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, rows.length)} of {rows.length}
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="gw-msg-prev" disabled={page <= 1} onClick={() => setPage(page - 1)}
            className="border border-zinc-200 rounded-md px-2.5 py-1 text-xs disabled:opacity-40 hover:bg-zinc-50">Prev</button>
          <span className="font-mono px-2">{page} / {totalPages}</span>
          <button data-testid="gw-msg-next" disabled={page >= totalPages} onClick={() => setPage(page + 1)}
            className="border border-zinc-200 rounded-md px-2.5 py-1 text-xs disabled:opacity-40 hover:bg-zinc-50">Next</button>
        </div>
      </div>
    </div>
  );
}

function FormModal({ title, initial, fields, selects = [], onCancel, onSave }) {
  const [form, setForm] = useState(initial || {});
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4" onClick={onCancel}>
      <div className="bg-white border border-zinc-200 rounded-lg w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-heading text-lg font-bold mb-3">{title}</h2>
        <form
          onSubmit={(e) => { e.preventDefault(); onSave(form); }}
          className="space-y-3"
        >
          {fields.map(([k, label]) => (
            <label key={k} className="block">
              <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">{label}</span>
              <input
                data-testid={`form-input-${k}`}
                className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3]"
                value={form[k] ?? ""}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              />
            </label>
          ))}
          {selects.map(([k, label, opts]) => (
            <label key={k} className="block">
              <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">{label}</span>
              <select
                className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
                value={form[k] ?? ""}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              >
                {opts.map(([id, lbl]) => <option key={id} value={id}>{lbl}</option>)}
              </select>
            </label>
          ))}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onCancel} className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm hover:bg-zinc-50">Cancel</button>
            <button data-testid="form-save" type="submit" className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm hover:bg-indigo-900">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}
